import React, { useState } from "react";
import {
  Popover,
  Button,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import {
  DATE_RANGE_MENU_GROUPS,
  DATE_RANGE_PRESETS,
  DateRangePreset,
} from "../../types/reports";
import { dateRangeLabel } from "../../utils/dateRangeFormat";
import styles from "./DateRangeFilter.module.css";

// `dateRangeLabel` moved to utils/dateRangeFormat so the shared Reports / Sales
// chips can use it without importing this component; re-exported here so every
// existing `from "./DateRangeFilter"` import keeps working unchanged.
export { dateRangeLabel };

interface Props {
  preset: DateRangePreset;
  /** "YYYY-MM-DD" string — kept as a string end-to-end so we never cross
   * a timezone boundary. `new Date("2024-03-15")` would be UTC midnight,
   * which formats back as Mar 14 in any timezone west of UTC. */
  customStart: string | null;
  customEnd: string | null;
  onChange: (
    preset: DateRangePreset,
    customStart: string | null,
    customEnd: string | null,
  ) => void;
  disabled?: boolean;
}

interface MenuProps {
  preset: DateRangePreset;
  customStart: string | null;
  customEnd: string | null;
  onChange: (
    preset: DateRangePreset,
    customStart: string | null,
    customEnd: string | null,
  ) => void;
  /** Called after a non-custom preset is picked, to dismiss the popover. */
  onClose: () => void;
  /**
   * Limit the menu to these presets (e.g. a v2 report definition's
   * `date_range_presets`). Presets outside the standard columns are listed
   * under "Previous". Custom range is always offered. Omit for the standard
   * menu.
   */
  presets?: ReadonlyArray<DateRangePreset>;
}

type MenuGroup = (typeof DATE_RANGE_MENU_GROUPS)[number];

const EXTRA_GROUP_LABEL = "Previous";

/** The standard menu columns filtered to `presets`, plus any allowed preset
 *  the standard columns don't list. */
export function menuGroupsFor(
  presets?: ReadonlyArray<DateRangePreset>,
): ReadonlyArray<MenuGroup> {
  if (!presets) return DATE_RANGE_MENU_GROUPS;
  const allowed = new Set(presets);
  const groups = DATE_RANGE_MENU_GROUPS.map((g) => ({
    label: g.label,
    options: g.options.filter((o) => allowed.has(o.value)),
  }));
  const listed = new Set(groups.flatMap((g) => g.options.map((o) => o.value)));
  const extra = DATE_RANGE_PRESETS.filter(
    (p) =>
      allowed.has(p.value) && !listed.has(p.value) && p.value !== "fixed_dates",
  );
  return [...groups, { label: EXTRA_GROUP_LABEL, options: extra }].filter(
    (g) => g.options.length > 0,
  );
}

/**
 * The date-range menu body: preset columns + a custom-range picker. Shared by
 * the reports `DateRangeFilter` (Button trigger) and the Sales date pill so
 * both use the exact same inner contents.
 */
export function DateRangeMenuContent({
  preset,
  customStart,
  customEnd,
  onChange,
  onClose,
  presets,
}: MenuProps) {
  const handlePresetClick = (next: DateRangePreset) => {
    if (next === "fixed_dates") {
      // Switch into custom mode but keep the popover open for start/end.
      onChange("fixed_dates", customStart, customEnd);
      return;
    }
    onChange(next, null, null);
    onClose();
  };

  return (
    <Group gap={0} align="stretch" wrap="nowrap">
      {menuGroupsFor(presets).map((group) => (
        <Stack key={group.label} className={styles.column} gap={2} p="xs">
          <Text size="xs" c="dimmed" fw={600} px={10} pt={4}>
            {group.label}
          </Text>
          {group.options.map((opt) => (
            <UnstyledButton
              key={opt.value}
              className={
                preset === opt.value
                  ? `${styles.option} ${styles.optionSelected}`
                  : styles.option
              }
              onClick={() => handlePresetClick(opt.value)}
            >
              {opt.label}
            </UnstyledButton>
          ))}
        </Stack>
      ))}
      <Stack
        className={`${styles.column} ${styles.columnCustom}`}
        gap={2}
        p="xs"
      >
        <Text size="xs" c="dimmed" fw={600} px={10} pt={4}>
          Custom
        </Text>
        <UnstyledButton
          className={
            preset === "fixed_dates"
              ? `${styles.option} ${styles.optionSelected}`
              : styles.option
          }
          onClick={() => handlePresetClick("fixed_dates")}
        >
          Custom range
        </UnstyledButton>
        {preset === "fixed_dates" && (
          <Stack gap="xs" mt="xs" px={4}>
            <DatePickerInput
              label="Start"
              size="xs"
              placeholder="Pick start"
              value={customStart}
              onChange={(v) => onChange("fixed_dates", v ?? null, customEnd)}
              maxDate={customEnd ?? undefined}
              popoverProps={{ withinPortal: false, zIndex: 400 }}
            />
            <DatePickerInput
              label="End"
              size="xs"
              placeholder="Pick end"
              value={customEnd}
              onChange={(v) => onChange("fixed_dates", customStart, v ?? null)}
              minDate={customStart ?? undefined}
              popoverProps={{ withinPortal: false, zIndex: 400 }}
            />
            {(!customStart || !customEnd) && (
              <Text size="xs" c="dimmed">
                Pick both a start and end date to run the report.
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Group>
  );
}

export default function DateRangeFilter({
  preset,
  customStart,
  customEnd,
  onChange,
  disabled = false,
}: Props) {
  const [opened, setOpened] = useState(false);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal
      shadow="md"
      closeOnClickOutside
    >
      <Popover.Target>
        <Button
          variant="default"
          leftSection={<IconCalendar size={14} />}
          rightSection={<IconChevronDown size={14} />}
          disabled={disabled}
          onClick={() => setOpened((o) => !o)}
        >
          {dateRangeLabel(preset, customStart, customEnd)}
        </Button>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <DateRangeMenuContent
          preset={preset}
          customStart={customStart}
          customEnd={customEnd}
          onChange={onChange}
          onClose={() => setOpened(false)}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
